import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  CreditCard,
  Crown,
  Zap,
  TrendingUp,
  Calendar,
  ExternalLink,
  FileText,
  Download,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useSubscription } from '@/hooks/useSubscription';
import { usePaymentProvider } from '@/hooks/usePaymentProvider';
import { PlanBadge } from '@/components/subscription/PlanBadge';
import { formatLimit } from '@/lib/plan-limits';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';

export function BillingSection() {
  const {
    subscription,
    usage,
    invoices,
    plan,
    limits,
    isLoading,
    isLoadingInvoices,
    getDynamicQRUsagePercent,
    getScansUsagePercent,
    isTrialing,
    isPastDue,
    willCancelAtPeriodEnd,
    getDaysRemaining,
    getTrialDaysRemaining,
  } = useSubscription();
  
  const { openCustomerPortal, isOpeningPortal, paymentConfig } = usePaymentProvider();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const dynamicQRPercent = getDynamicQRUsagePercent();
  const scansPercent = getScansUsagePercent();

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-primary" />
            Current Plan
          </CardTitle>
          <CardDescription>
            Your subscription details and billing information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <PlanBadge plan={plan} className="text-base px-3 py-1" />
              <div>
                <h3 className="font-semibold text-lg">{limits.displayName} Plan</h3>
                <p className="text-sm text-muted-foreground">
                  {plan === 'free' ? 'Free forever' : `$${limits.monthlyPrice}/month`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isTrialing && (
                <Badge variant="secondary" className="bg-warning/10 text-warning">
                  Trial: {getTrialDaysRemaining()} days left
                </Badge>
              )}
              {isPastDue && (
                <Badge variant="destructive">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  Payment Past Due
                </Badge>
              )}
              {willCancelAtPeriodEnd && (
                <Badge variant="outline" className="text-warning border-warning">
                  Cancels in {getDaysRemaining()} days
                </Badge>
              )}
            </div>
          </div>

          {subscription?.current_period_end && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>
                {willCancelAtPeriodEnd ? 'Access until' : 'Renews'}{' '}
                {format(new Date(subscription.current_period_end), 'MMMM d, yyyy')}
              </span>
            </div>
          )}

          <div className="flex gap-3">
            {plan !== 'enterprise' && (
              <Button onClick={() => navigate('/pricing')}>
                <TrendingUp className="h-4 w-4 mr-2" />
                {plan === 'free' ? 'Upgrade Plan' : 'Change Plan'}
              </Button>
            )}
            {paymentConfig?.stripe && plan !== 'free' && (
              <Button
                variant="outline"
                onClick={() => openCustomerPortal()}
                disabled={isOpeningPortal}
              >
                {isOpeningPortal ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CreditCard className="h-4 w-4 mr-2" />
                )}
                Manage Billing
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Usage Stats Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Usage This Month
          </CardTitle>
          <CardDescription>
            Track your feature usage against plan limits
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Dynamic QR Codes */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Dynamic QR Codes</span>
              <span className="text-muted-foreground">
                {usage?.dynamic_qr_count || 0} / {formatLimit(limits.dynamicQRLimit)}
              </span>
            </div>
            <Progress
              value={dynamicQRPercent}
              className={cn(
                'h-2',
                dynamicQRPercent >= 90 && 'bg-red-100 [&>div]:bg-red-500'
              )}
            />
          </div>

          {/* Scans */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium">Monthly Scans</span>
              <span className="text-muted-foreground">
                {usage?.scans_count || 0} / {formatLimit(limits.scansPerMonth)}
              </span>
            </div>
            <Progress
              value={scansPercent}
              className={cn(
                'h-2',
                scansPercent >= 90 && 'bg-red-100 [&>div]:bg-red-500'
              )}
            />
          </div>

          {/* Batch Jobs */}
          {limits.batchLimit > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Batch Operations</span>
                <span className="text-muted-foreground">
                  {usage?.batch_count || 0} / {formatLimit(limits.batchLimit)}
                </span>
              </div>
              <Progress
                value={limits.batchLimit === -1 ? 0 : ((usage?.batch_count || 0) / limits.batchLimit) * 100}
                className="h-2"
              />
            </div>
          )}

          {/* AI Suggestions */}
          {limits.aiSuggestionsPerMonth > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">AI Design Suggestions</span>
                <span className="text-muted-foreground">
                  {usage?.ai_suggestions_count || 0} / {formatLimit(limits.aiSuggestionsPerMonth)}
                </span>
              </div>
              <Progress
                value={limits.aiSuggestionsPerMonth === -1 ? 0 : ((usage?.ai_suggestions_count || 0) / limits.aiSuggestionsPerMonth) * 100}
                className="h-2"
              />
            </div>
          )}

          {/* API Requests */}
          {limits.apiRequestsPerMonth > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">API Requests</span>
                <span className="text-muted-foreground">
                  {usage?.api_requests_count || 0} / {formatLimit(limits.apiRequestsPerMonth)}
                </span>
              </div>
              <Progress
                value={limits.apiRequestsPerMonth === -1 ? 0 : ((usage?.api_requests_count || 0) / limits.apiRequestsPerMonth) * 100}
                className="h-2"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice History Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Invoice History
          </CardTitle>
          <CardDescription>
            View and download your past payment receipts
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingInvoices ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No invoices yet</p>
              <p className="text-sm">Your payment history will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {invoices.map((invoice) => (
                <motion.div
                  key={invoice.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-between p-4 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {format(new Date(invoice.created_at), 'MMMM d, yyyy')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {invoice.currency.toUpperCase()} {(invoice.amount_paid / 100).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={invoice.status === 'paid' ? 'default' : 'secondary'}
                      className={cn(
                        invoice.status === 'paid' && 'bg-primary/10 text-primary border-primary/20'
                      )}
                    >
                      {invoice.status}
                    </Badge>
                    {invoice.invoice_pdf && (
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                      >
                        <a href={invoice.invoice_pdf} target="_blank" rel="noopener noreferrer">
                          <Download className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    {invoice.hosted_invoice_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                      >
                        <a href={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
